import { useEffect, useRef, useState, useCallback } from "react";
import {
  Box, Sheet, Card, Typography, Input, Button, Chip, Alert, CircularProgress,
  Divider, Select, Option,
} from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { adminApi } from "../adminApi";

const STATUS_COLOR = {
  new: "neutral", paid: "success", printing: "primary",
  shipped: "primary", done: "success", cancelled: "danger",
};
const PROC_COLOR = { done: "success", failed: "danger", new: "neutral", in_progress: "primary" };

const fmt = (n) => Number(n ?? 0).toLocaleString("ru-RU");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU") : "—");
const money = (n) => `${Number(n || 0).toLocaleString("ru-RU")} ₽`;
const fullName = (o) => [o.client_name, o.client_last_name].filter(Boolean).join(" ") || "—";

function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.25 }}>
      <Typography level="body-xs" color="neutral">{label}</Typography>
      <Typography level="body-xs" sx={{ textAlign: "right", wordBreak: "break-word" }}>{value}</Typography>
    </Box>
  );
}

function StatCard({ label, children }) {
  return (
    <Card variant="soft" sx={{ p: 1.5, gap: 0.5, minWidth: 0 }}>
      <Typography level="title-md" color="neutral" sx={{ fontWeight: 700 }}>{label}</Typography>
      {children}
    </Card>
  );
}

function OrderDetail({ detail, loading }) {
  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size="sm" /></Box>;
  }
  if (!detail) return null;

  const promoValue = detail.promo
    ? `${detail.promo.code} · ${detail.promo.free_delivery
        ? "беспл. доставка"
        : (detail.promo.discount_type === "percent" ? `${detail.promo.percent}%` : money(detail.promo.amount))}`
    : null;

  return (
    <Box sx={{ pt: 1.5 }}>
      <Divider sx={{ mb: 1.5 }} />

      <Typography level="title-sm" sx={{ mb: 0.5 }}>Позиции ({detail.items?.length || 0})</Typography>
      {(detail.items || []).map((it) => (
        <Sheet key={it.id} variant="soft" sx={{ borderRadius: "8px", p: 1, mb: 0.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{it.book_title}</Typography>
            <Typography level="body-sm" sx={{ whiteSpace: "nowrap" }}>
              {it.quantity} × {money(it.price)} = {money(it.sum)}
            </Typography>
          </Box>
          <Typography level="body-xs" color="neutral">
            {[it.size, it.pages_count ? `${it.pages_count} стр.` : ""].filter(Boolean).join(" · ")}
          </Typography>
        </Sheet>
      ))}

      <Typography level="title-sm" sx={{ mt: 1.5, mb: 0.5 }}>Стоимость</Typography>
      <Row label="Скидка" value={detail.discount_amount ? `− ${money(detail.discount_amount)} (${detail.discount_percent || 0}%)` : "—"} />
      <Row label="Доставка" value={money(detail.delivery_price)} />
      <Row label="Итого" value={money(detail.total_price)} />

      {(detail.promo || detail.discount || detail.gift) && (
        <>
          <Typography level="title-sm" sx={{ mt: 1.5, mb: 0.5 }}>Применения</Typography>
          <Row label="Промокод" value={promoValue} />
          {detail.discount && <Row label="Скидка" value={`${detail.discount.title} · ${detail.discount.percent}%`} />}
          {detail.gift && <Row label="Подарок" value={`${detail.gift.token} · ${detail.gift.status_display}`} />}
        </>
      )}

      <Typography level="title-sm" sx={{ mt: 1.5, mb: 0.5 }}>Доставка</Typography>
      <Row label="Тип" value={detail.delivery_type_display} />
      <Row label="Город" value={detail.delivery_city} />
      <Row label="Адрес" value={detail.delivery_address} />
      <Row label="ПВЗ" value={detail.delivery_pvz_address || detail.delivery_pvz_code} />
      <Row label="Статус СДЭК" value={detail.cdek_status_code} />
      <Row label="Комментарий" value={detail.delivery_comment} />

      {detail.processes?.length > 0 && (
        <>
          <Typography level="title-sm" sx={{ mt: 1.5, mb: 0.5 }}>Процессы</Typography>
          {detail.processes.map((p) => (
            <Box key={p.id} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography level="body-xs">{p.type}</Typography>
              <Box sx={{ textAlign: "right" }}>
                <Chip size="sm" variant="soft" color={PROC_COLOR[p.status] || "primary"}>{p.status_display}</Chip>
                {p.error_message && <Typography level="body-xs" color="danger">{p.error_message}</Typography>}
                <Typography level="body-xs" color="neutral">{fmtDate(p.created_at)}</Typography>
              </Box>
            </Box>
          ))}
        </>
      )}

      <Typography level="title-sm" sx={{ mt: 1.5, mb: 0.5 }}>Клиент</Typography>
      <Row label="Email" value={detail.client_email} />
      <Row label="Телефон" value={detail.client_phone} />

      {detail.admin_url && (
        <Box sx={{ mt: 1 }}>
          <Button size="sm" variant="soft" component="a" href={detail.admin_url} target="_blank">
            Открыть в Django-админке
          </Button>
        </Box>
      )}
    </Box>
  );
}

function OrderCard({ order, expanded, onToggle, detail, detailLoading }) {
  return (
    <Card variant="outlined" sx={{ p: 0, overflow: "hidden" }}>
      <Box onClick={onToggle} sx={{ p: 1.5, cursor: "pointer", "&:hover": { bgcolor: "background.level1" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography level="title-sm">№ {order.order_number || "—"}</Typography>
          <Chip size="sm" variant="soft" color={STATUS_COLOR[order.status] || "neutral"}>{order.status_display}</Chip>
          <Box sx={{ flex: 1 }} />
          <Typography level="title-sm">{money(order.total_price)}</Typography>
          <KeyboardArrowDownRoundedIcon sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: ".2s" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mt: 0.5 }}>
          <Typography level="body-sm">{fullName(order)}</Typography>
          <Typography level="body-xs" color="neutral">{order.client_phone}</Typography>
          {order.delivery_city && <Typography level="body-xs" color="neutral">· {order.delivery_city}</Typography>}
          <Box sx={{ flex: 1 }} />
          <Typography level="body-xs" color="neutral">{fmtDate(order.created_at)}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
          <Chip size="sm" variant="outlined" color="neutral">{order.items_count} поз.</Chip>
          <Chip size="sm" variant="outlined" color="neutral">{order.delivery_type_display}</Chip>
          {order.has_promo && <Chip size="sm" variant="soft" color="success">промокод</Chip>}
          {order.has_gift && <Chip size="sm" variant="soft" color="warning">подарок</Chip>}
          {order.has_discount && <Chip size="sm" variant="soft" color="primary">скидка</Chip>}
        </Box>
      </Box>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: "hidden" }}
          >
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <OrderDetail detail={detail} loading={detailLoading} />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [statusChoices, setStatusChoices] = useState([]);
  const [stats, setStats] = useState(null);

  // применённые фильтры (перезагружают список)
  const [status, setStatus] = useState("");
  const [city, setCity] = useState("");
  const [sumMin, setSumMin] = useState("");
  const [sumMax, setSumMax] = useState("");
  const [q, setQ] = useState("");
  // черновики полей (применяются по «Найти» / Enter)
  const [qInput, setQInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [sumMinInput, setSumMinInput] = useState("");
  const [sumMaxInput, setSumMaxInput] = useState("");

  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState({});

  const sentinelRef = useRef(null);
  const LIMIT = 20;

  const buildFilters = useCallback(() => {
    const f = {};
    if (status) f.status = status;
    if (city) f.city = city;
    if (sumMin) f.sum_min = sumMin;
    if (sumMax) f.sum_max = sumMax;
    if (q) f.q = q;
    return f;
  }, [status, city, sumMin, sumMax, q]);

  const load = useCallback(async (reset) => {
    const offset = reset ? 0 : nextOffset;
    if (reset) setLoading(true); else setLoadingMore(true);
    setError("");
    try {
      const filters = buildFilters();
      const data = await adminApi.ordersList({ limit: LIMIT, offset, ...filters });
      setStatusChoices(data.status_choices || []);
      setCount(data.count || 0);
      setHasMore(!!data.has_more);
      setNextOffset(data.next_offset != null ? data.next_offset : offset + LIMIT);
      setOrders((prev) => (reset ? data.results : [...prev, ...data.results]));
      if (reset) {
        adminApi.ordersStats(filters).then(setStats).catch(() => setStats(null));
      }
    } catch (e) {
      setError(e.message || "Ошибка загрузки заказов");
    } finally {
      if (reset) setLoading(false); else setLoadingMore(false);
    }
  }, [nextOffset, buildFilters]);

  // Первичная загрузка и перезагрузка при смене применённых фильтров
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, city, sumMin, sumMax, q]);

  // Бесконечная прокрутка
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
        load(false);
      }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, load]);

  const toggle = async (order) => {
    const id = order.id;
    const willExpand = expandedId !== id;
    setExpandedId((cur) => (cur === id ? null : id));
    if (willExpand && !details[id]) {
      setDetailLoading((m) => ({ ...m, [id]: true }));
      try {
        const d = await adminApi.orderDetail(id);
        setDetails((m) => ({ ...m, [id]: d }));
      } catch (e) {
        setDetails((m) => ({ ...m, [id]: { ...order, _error: e.message } }));
      } finally {
        setDetailLoading((m) => ({ ...m, [id]: false }));
      }
    }
  };

  const applySearch = () => {
    setQ(qInput.trim());
    setCity(cityInput.trim());
    setSumMin(sumMinInput.trim());
    setSumMax(sumMaxInput.trim());
  };

  return (
    <Box>
      <Typography level="h2" sx={{ mb: 0.5 }}>Заказы</Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
        Всего: {count}. Прокрутите вниз — подгрузятся ещё.
      </Typography>

      {/* Статистика (с учётом фильтров) */}
      {stats && (
        <Box sx={{
          display: "grid", gap: 1, mb: 2, alignItems: "start",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}>
          <StatCard label="Топ городов по заказам">
            {(stats.top_cities || []).length === 0 ? (
              <Typography level="body-xs" color="neutral">—</Typography>
            ) : (
              stats.top_cities.slice(0, 5).map((c, i) => (
                <Typography key={c.city} level="body-xs" noWrap>
                  {i + 1}. {c.city} — <b>{fmt(c.count)}</b>
                </Typography>
              ))
            )}
          </StatCard>
          <StatCard label="Сумма заказов">
            <Typography level="title-lg">{money(stats.total_sum)}</Typography>
          </StatCard>
          <StatCard label="Клиентов">
            <Typography level="title-lg">{fmt(stats.clients_count)}</Typography>
          </StatCard>
          <StatCard label="Городов">
            <Typography level="title-lg">{fmt(stats.cities_count)}</Typography>
          </StatCard>
        </Box>
      )}

      {/* Фильтры */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Input
          size="sm"
          placeholder="Номер, имя, телефон, email"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
          startDecorator={<SearchRoundedIcon />}
          sx={{ minWidth: 220 }}
        />
        <Input
          size="sm"
          placeholder="Город"
          value={cityInput}
          onChange={(e) => setCityInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
          sx={{ minWidth: 140 }}
        />
        <Input
          size="sm" type="number" placeholder="Сумма от"
          value={sumMinInput}
          onChange={(e) => setSumMinInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
          sx={{ width: 120 }}
        />
        <Input
          size="sm" type="number" placeholder="Сумма до"
          value={sumMaxInput}
          onChange={(e) => setSumMaxInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
          sx={{ width: 120 }}
        />
        <Select size="sm" placeholder="Статус" value={status} onChange={(_, v) => setStatus(v ?? "")} sx={{ minWidth: 150 }}>
          <Option value="">Все статусы</Option>
          {statusChoices.map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
        </Select>
        <Button size="sm" variant="soft" onClick={applySearch}>Найти</Button>
      </Box>

      {error && <Alert color="danger" variant="soft" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
      ) : orders.length === 0 ? (
        <Typography level="body-sm" color="neutral">Заказы не найдены.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              expanded={expandedId === o.id}
              onToggle={() => toggle(o)}
              detail={details[o.id]}
              detailLoading={!!detailLoading[o.id]}
            />
          ))}
        </Box>
      )}

      <Box ref={sentinelRef} sx={{ height: 1 }} />
      {loadingMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size="sm" /></Box>
      )}
      {!hasMore && !loading && orders.length > 0 && (
        <Typography level="body-xs" color="neutral" sx={{ textAlign: "center", py: 2 }}>Это все заказы.</Typography>
      )}
    </Box>
  );
}